import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { ThunkDispatchType } from './types';
import { RootAction, RootState } from '.';
import { selectLeadersWithCalendars } from './schedule/selectors';
import { AllOAuthGrantDetails } from '.';

// Calendar grant checking logic (moved here to avoid circular imports)
const checkForCalendarGrants = (oauth_grant_details: AllOAuthGrantDetails): boolean => {
  return Object.keys(oauth_grant_details).some(provider => {
    if (provider === 'google' || provider === 'microsoft') {
      return Object.keys(oauth_grant_details[provider]).length > 0;
    }
    return false;
  });
};

export const useWithDispatch = <Args extends unknown[], T>(
  fn: (...args: Args) => ThunkAction<T, RootState, null, RootAction>
) => {
// export const useWithDispatch = <T>(fn: ActionCreator<ThunkAction<T, RootState, null, RootAction>>) => {
// export const useWithDispatch = <T extends string, Payload>(fn: ActionCreatorWithPayload<Payload, T>) => {
  const dispatch = useDispatch<ThunkDispatchType>();

  return useCallback((...args: Args) => dispatch(fn(...args)), [dispatch, fn]);
  // return useCallback((args: Payload) => dispatch(fn(args)), [dispatch, fn]);
};

export const useHandler = () => {
  const dispatch = useDispatch();
  const handler = useCallback(
    <TArg extends unknown[], TAction extends UnknownAction>(actionCreator: (...args: TArg) => TAction) => 
      (...args: TArg) => dispatch(actionCreator(...args)),
    [dispatch]
  );
  return handler;
};

export enum CalendarConnectionStatus {
  NO_CALENDAR_GRANT = 'NO_CALENDAR_GRANT',
  NO_LEADER_ASSOCIATIONS = 'NO_LEADER_ASSOCIATIONS',
  CONNECTED = 'CONNECTED'
}

export const useCalendarsConnected = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const leadersWithCalendars = useSelector(selectLeadersWithCalendars);

  // Add safety checks to prevent crashes
  const hasCalendarGrant = user && user.oauth_grant_details && checkForCalendarGrants(user.oauth_grant_details);
  const hasLeadersWithCalendars = leadersWithCalendars && leadersWithCalendars.length > 0;

  if (!hasCalendarGrant) {
    return CalendarConnectionStatus.NO_CALENDAR_GRANT;
  }
  
  if (!hasLeadersWithCalendars) {
    return CalendarConnectionStatus.NO_LEADER_ASSOCIATIONS;
  }
  
  return CalendarConnectionStatus.CONNECTED;
};
